/**
 * SipAgentWorker - Client-Side SIP Registration Worker
 *
 * Gerencia registro SIP por agente (PER_AGENT mode) usando JsSIP + WebRTC
 *
 * Features:
 * - Registro SIP individual por agente
 * - WebRTC audio para chamadas
 * - Sincronização de estado com servidor via WebSocket
 * - Criação automática de conversas no Chatwoot
 * - Gestão de chamadas (inbound/outbound)
 */

import JsSIP from 'jssip';
import { authenticatedFetch } from '../utils/authHeaders';

const SIP_USER_AGENT = 'Hermes SIP Channel';
const SIP_REGISTER_EXPIRES_SECONDS = 1800;
const SIP_CONNECTION_RECOVERY_MIN_SECONDS = 10;
const SIP_CONNECTION_RECOVERY_MAX_SECONDS = 120;

export class SipAgentWorker {
  constructor({
    server,
    extension,
    password,
    realm,
    transport,
    inboxId,
    accountId,
  }) {
    this.server = server;
    this.extension = extension;
    this.password = password;
    this.realm = realm;
    this.transport = transport; // wss:// or ws://
    this.inboxId = inboxId;
    this.accountId = accountId;
    this.ua = null;
    this.currentCall = null;
    this.state = 'disconnected'; // disconnected, connecting, registered, ringing, calling, in_call, failed
    this.pendingOutgoingCallRequest = null;
    this.pendingOutgoingCalls = new Map();

    console.log(
      '[PLUGIN][SIP-CHANNEL] Worker initialized for extension:',
      this.extension
    );
  }

  /**
   * Conecta e registra o ramal SIP
   */
  async connect() {
    console.log(
      '[PLUGIN][SIP-CHANNEL] Connecting agent extension:',
      this.extension
    );

    try {
      const socket = new JsSIP.WebSocketInterface(this.transport);

      const configuration = {
        sockets: [socket],
        uri: `sip:${this.extension}@${this.realm}`,
        password: this.password,
        display_name: this.extension,
        register: true,
        register_expires: SIP_REGISTER_EXPIRES_SECONDS,
        session_timers: true,
        session_timers_refresh_method: 'invite',
        connection_recovery_max_interval: SIP_CONNECTION_RECOVERY_MAX_SECONDS,
        connection_recovery_min_interval: SIP_CONNECTION_RECOVERY_MIN_SECONDS,
        user_agent: SIP_USER_AGENT,
      };

      this.ua = new JsSIP.UA(configuration);

      // Eventos de registro
      this.ua.on('registered', () => this.onRegistered());
      this.ua.on('unregistered', () => this.onUnregistered());
      this.ua.on('registrationFailed', e => this.onRegistrationFailed(e));

      // Eventos de chamada
      this.ua.on('newRTCSession', e => this.onNewCall(e));

      // Eventos de conexão
      this.ua.on('connected', () => {
        console.log('[PLUGIN][SIP-CHANNEL] WebSocket connected');
      });

      this.ua.on('disconnected', () => {
        console.log('[PLUGIN][SIP-CHANNEL] WebSocket disconnected');
      });

      this.ua.start();
      this.updateState('connecting');
    } catch (error) {
      console.error('[PLUGIN][SIP-CHANNEL] Error connecting:', error);
      this.updateState('failed');
      throw error;
    }
  }

  /**
   * Callback: Registro SIP bem-sucedido
   */
  onRegistered() {
    console.log('[PLUGIN][SIP-CHANNEL] Extension registered successfully');
    this.updateState('registered');

    // Sincroniza com servidor Rails
    this.syncStateWithServer({
      status: 'registered',
      extension: this.extension,
      registered_at: new Date().toISOString(),
    });
  }

  /**
   * Callback: Registro SIP removido
   */
  onUnregistered() {
    console.log('[PLUGIN][SIP-CHANNEL] Extension unregistered');
    this.updateState('disconnected');

    this.syncStateWithServer({
      status: 'disconnected',
      extension: this.extension,
      unregistered_at: new Date().toISOString(),
    });
  }

  /**
   * Callback: Falha no registro SIP
   */
  onRegistrationFailed(event) {
    console.error('[PLUGIN][SIP-CHANNEL] Registration failed:', event.cause);
    this.updateState('failed');

    this.syncStateWithServer({
      status: 'failed',
      extension: this.extension,
      error: event.cause,
      failed_at: new Date().toISOString(),
    });

    // Notifica UI sobre erro
    this.notifyError('Falha ao registrar ramal SIP: ' + event.cause);
  }

  /**
   * Callback: Nova sessão RTC (chamada)
   */
  onNewCall(event) {
    const session = event.session;
    const isIncoming = session.direction === 'incoming';

    console.log('[PLUGIN][SIP-CHANNEL] New call:', {
      direction: session.direction,
      remote: session.remote_identity.uri.user,
    });

    if (isIncoming) {
      this.handleIncomingCall(session);
    } else {
      this.handleOutgoingCall(session);
    }
  }

  /**
   * Gerencia chamada recebida
   */
  handleIncomingCall(session) {
    this.currentCall = session;
    this.updateState('ringing');

    const callerId = session.remote_identity.uri.user;
    const callerName = session.remote_identity.display_name || callerId;

    console.log('[PLUGIN][SIP-CHANNEL] Incoming call from:', callerId);

    // Cria conversa no Chatwoot via API
    this.createConversationFromCall({
      call_id: session.id,
      caller_id: callerId,
      caller_name: callerName,
      direction: 'inbound',
      extension: this.extension,
    })
      .then(data => {
        console.log(
          '[PLUGIN][SIP-CHANNEL] Conversation created:',
          data.conversation_id
        );

        // Armazena conversation_id na sessão
        session.conversation_id = data.conversation_id;
        this.notifyCallCreated({
          callSid: session.id,
          conversationId: data.conversation_id,
          inboxId: this.inboxId,
          callDirection: 'inbound',
        });
      })
      .catch(error => {
        console.error(
          '[PLUGIN][SIP-CHANNEL] Error creating conversation:',
          error
        );
      });

    // Sincroniza estado
    this.syncStateWithServer({
      status: 'ringing',
      call_id: session.id,
      caller_id: callerId,
      extension: this.extension,
    });

    // Setup eventos da chamada
    this.setupCallEvents(session);

    // Notifica UI
    this.notifyIncomingCall({
      callSid: session.id,
      callerId,
      callerName,
      inboxId: this.inboxId,
      callDirection: 'inbound',
      sessionId: session.id,
    });
    this.notifyCallState({
      callSid: session.id,
      callerId,
      callerName,
      inboxId: this.inboxId,
      callDirection: 'inbound',
      status: 'ringing',
      isActive: false,
      isMuted: false,
    });
  }

  /**
   * Gerencia chamada realizada
   */
  handleOutgoingCall(session) {
    this.currentCall = session;
    this.updateState('calling');

    const calledNumber = session.remote_identity.uri.user;
    const pendingRequest = this.attachPendingOutgoingRequest(session);
    const callMetadata =
      session.chatwootMetadata || pendingRequest?.metadata || {};

    console.log('[PLUGIN][SIP-CHANNEL] Outgoing call to:', calledNumber);
    this.notifyCallState({
      callSid: session.id,
      calledNumber,
      inboxId: this.inboxId,
      callDirection: 'outbound',
      status: 'calling',
      isActive: true,
      isMuted: false,
    });

    // Cria conversa no Chatwoot via API
    this.createConversationFromCall({
      call_id: session.id,
      called_number: calledNumber,
      direction: 'outbound',
      extension: this.extension,
      contact_id: callMetadata.contactId,
    })
      .then(data => {
        console.log(
          '[PLUGIN][SIP-CHANNEL] Conversation created:',
          data.conversation_id
        );
        session.conversation_id = data.conversation_id;
        this.resolvePendingOutgoingCall(session.id, data);
        this.notifyCallCreated({
          callSid: session.id,
          conversationId: data.conversation_id,
          inboxId: this.inboxId,
          callDirection: 'outbound',
        });
        this.notifyCallState({
          callSid: session.id,
          conversationId: data.conversation_id,
          inboxId: this.inboxId,
          callDirection: 'outbound',
        });
      })
      .catch(error => {
        console.error(
          '[PLUGIN][SIP-CHANNEL] Error creating conversation:',
          error
        );
        this.rejectPendingOutgoingCall(session.id, error);
      });

    // Sincroniza estado
    this.syncStateWithServer({
      status: 'calling',
      call_id: session.id,
      called_number: calledNumber,
      extension: this.extension,
    });

    this.setupCallEvents(session);
  }

  /**
   * Configura eventos da sessão de chamada
   */
  setupCallEvents(session) {
    session.on('accepted', () => {
      console.log('[PLUGIN][SIP-CHANNEL] Call accepted');
      this.updateState('in_call');

      this.syncStateWithServer({
        status: 'in_call',
        call_id: session.id,
        answered_at: new Date().toISOString(),
      });
      this.syncCallWithServer(session.id, {
        status: 'in-progress',
        timestamp: this.currentTimestamp(),
      });
      this.notifyCallState({
        callSid: session.id,
        conversationId: session.conversation_id,
        inboxId: this.inboxId,
        callDirection: this.normalizeCallDirection(session.direction),
        status: 'in_call',
        isActive: true,
        isMuted: this.isMicrophoneMuted(session.id),
      });
    });

    session.on('ended', () => {
      console.log('[PLUGIN][SIP-CHANNEL] Call ended');
      this.updateState('registered');
      this.currentCall = null;

      this.syncStateWithServer({
        status: 'registered',
        call_id: session.id,
        ended_at: new Date().toISOString(),
      });
      this.syncCallWithServer(session.id, {
        status: 'completed',
        timestamp: this.currentTimestamp(),
      });
      this.notifyCallDisconnected({
        callSid: session.id,
        conversationId: session.conversation_id,
        inboxId: this.inboxId,
      });
    });

    session.on('failed', e => {
      console.error('[PLUGIN][SIP-CHANNEL] Call failed:', e.cause);
      this.updateState('registered');
      this.currentCall = null;
      const callStatus = this.mapFailureCauseToCallStatus(e.cause);

      this.syncStateWithServer({
        status: 'registered',
        call_id: session.id,
        failed_at: new Date().toISOString(),
        error: e.cause,
      });
      this.syncCallWithServer(session.id, {
        status: callStatus,
        timestamp: this.currentTimestamp(),
        error: e.cause,
      });
      this.rejectPendingOutgoingCall(session.id, e.cause);
      this.notifyCallDisconnected({
        callSid: session.id,
        conversationId: session.conversation_id,
        inboxId: this.inboxId,
        error: e.cause,
      });
    });

    session.on('muted', () => {
      this.notifyCallMediaState({
        callSid: session.id,
        inboxId: this.inboxId,
        isMuted: true,
      });
    });

    session.on('unmuted', () => {
      this.notifyCallMediaState({
        callSid: session.id,
        inboxId: this.inboxId,
        isMuted: false,
      });
    });

    // Anexa stream de áudio ao elemento HTML
    session.on('peerconnection', e => {
      const peerConnection = e.peerconnection;

      peerConnection.ontrack = event => {
        console.log('[PLUGIN][SIP-CHANNEL] Remote stream received');
        this.attachMediaStream(event.streams[0]);
      };
    });
  }

  /**
   * Cria conversa no Chatwoot a partir de dados da chamada
   */
  async createConversationFromCall(callData) {
    try {
      const response = await authenticatedFetch(
        `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/sip/calls`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call: callData,
            inbox_id: this.inboxId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to create conversation: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log(
        '[PLUGIN][SIP-CHANNEL] Conversation created:',
        data.conversation_id
      );

      return data;
    } catch (error) {
      console.error(
        '[PLUGIN][SIP-CHANNEL] Error creating conversation:',
        error
      );
      throw error;
    }
  }

  /**
   * Sincroniza estado do agente com servidor Rails
   */
  async syncStateWithServer(stateData) {
    try {
      const response = await authenticatedFetch(
        `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/sip_agent_state`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            state: stateData,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to sync state: ${response.statusText}`);
      }

      console.log(
        '[PLUGIN][SIP-CHANNEL] State synced with server:',
        stateData.status
      );
    } catch (error) {
      console.error('[PLUGIN][SIP-CHANNEL] Failed to sync state:', error);
    }
  }

  /**
   * Sincroniza status da chamada com o servidor Rails
   */
  async syncCallWithServer(callId, payload) {
    if (!callId) return null;

    try {
      const response = await authenticatedFetch(
        `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/sip/calls/${encodeURIComponent(callId)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to sync call: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('[PLUGIN][SIP-CHANNEL] Failed to sync call:', error);
      return null;
    }
  }

  /**
   * Atualiza estado interno e notifica componentes
   */
  updateState(newState) {
    const oldState = this.state;
    this.state = newState;

    console.log(
      '[PLUGIN][SIP-CHANNEL] State changed:',
      oldState,
      '→',
      newState
    );

    // Emite evento para componentes Vue
    window.dispatchEvent(
      new CustomEvent('sip:state-changed', {
        detail: {
          oldState,
          newState,
          extension: this.extension,
          inboxId: this.inboxId,
        },
      })
    );
  }

  /**
   * Anexa stream de mídia ao elemento de áudio
   */
  // eslint-disable-next-line class-methods-use-this
  attachMediaStream(stream) {
    let audioElement = document.getElementById('sip-remote-audio');

    if (!audioElement) {
      audioElement = document.createElement('audio');
      audioElement.id = 'sip-remote-audio';
      audioElement.autoplay = true;
      document.body.appendChild(audioElement);
    }

    audioElement.srcObject = stream;
    console.log('[PLUGIN][SIP-CHANNEL] Media stream attached to audio element');
  }

  /**
   * Notifica UI sobre chamada recebida
   */
  // eslint-disable-next-line class-methods-use-this
  notifyIncomingCall(callData) {
    window.dispatchEvent(
      new CustomEvent('sip:incoming-call', {
        detail: callData,
      })
    );
  }

  // eslint-disable-next-line class-methods-use-this
  notifyCallCreated(callData) {
    window.dispatchEvent(
      new CustomEvent('sip:call-created', {
        detail: callData,
      })
    );
  }

  // eslint-disable-next-line class-methods-use-this
  notifyCallState(callData) {
    window.dispatchEvent(
      new CustomEvent('sip:call-state', {
        detail: callData,
      })
    );
  }

  // eslint-disable-next-line class-methods-use-this
  notifyCallMediaState(callData) {
    window.dispatchEvent(
      new CustomEvent('sip:call-media-state', {
        detail: callData,
      })
    );
  }

  // eslint-disable-next-line class-methods-use-this
  notifyCallDisconnected(callData) {
    window.dispatchEvent(
      new CustomEvent('sip:call-disconnected', {
        detail: callData,
      })
    );
  }

  /**
   * Notifica UI sobre erro
   */
  // eslint-disable-next-line class-methods-use-this
  notifyError(message) {
    window.dispatchEvent(
      new CustomEvent('sip:error', {
        detail: { message },
      })
    );
  }

  /**
   * Desconecta e remove registro SIP
   */
  disconnect() {
    console.log('[PLUGIN][SIP-CHANNEL] Disconnecting...');

    if (this.currentCall) {
      this.hangupCall();
    }

    if (this.ua) {
      this.ua.stop();
      this.ua = null;
    }

    this.updateState('disconnected');

    this.syncStateWithServer({
      status: 'disconnected',
      extension: this.extension,
      disconnected_at: new Date().toISOString(),
    });
  }

  // ==================== Métodos de Controle de Chamada ====================

  /**
   * Atende chamada recebida
   */
  answerCall(callId = null) {
    if (
      this.currentCall &&
      this.state === 'ringing' &&
      (!callId || this.currentCall.id === callId)
    ) {
      console.log('[PLUGIN][SIP-CHANNEL] Answering call...');

      this.currentCall.answer({
        mediaConstraints: {
          audio: true,
          video: false,
        },
      });
    } else {
      console.warn('[PLUGIN][SIP-CHANNEL] Cannot answer: no ringing call');
    }
  }

  /**
   * Desliga chamada atual
   */
  hangupCall(callId = null) {
    if (this.currentCall && (!callId || this.currentCall.id === callId)) {
      console.log('[PLUGIN][SIP-CHANNEL] Hanging up call...');
      this.currentCall.terminate();
    } else {
      console.warn('[PLUGIN][SIP-CHANNEL] Cannot hangup: no active call');
    }
  }

  toggleMicrophoneMute(callId = null) {
    if (!(this.currentCall && (!callId || this.currentCall.id === callId))) {
      console.warn('[PLUGIN][SIP-CHANNEL] Cannot toggle mute: no active call');
      return false;
    }

    if (this.isMicrophoneMuted(callId)) {
      this.currentCall.unmute({ audio: true });
    } else {
      this.currentCall.mute({ audio: true });
    }

    return this.isMicrophoneMuted(callId);
  }

  isMicrophoneMuted(callId = null) {
    if (!(this.currentCall && (!callId || this.currentCall.id === callId))) {
      return false;
    }

    return Boolean(this.currentCall.isMuted?.().audio);
  }

  /**
   * Realiza chamada para número
   */
  makeCall(number, metadata = {}) {
    if (!(this.ua && this.state === 'registered')) {
      const error = new Error('Ramal não registrado. Aguarde conexão.');
      console.warn('[PLUGIN][SIP-CHANNEL] Cannot make call: not registered');
      this.notifyError(error.message);
      return Promise.reject(error);
    }

    console.log('[PLUGIN][SIP-CHANNEL] Making call to:', number);

    return new Promise((resolve, reject) => {
      const request = { metadata, resolve, reject };
      this.pendingOutgoingCallRequest = request;

      try {
        const session = this.ua.call(`sip:${number}@${this.realm}`, {
          mediaConstraints: {
            audio: true,
            video: false,
          },
        });

        if (!session) {
          throw new Error('Failed to create SIP call session');
        }

        session.chatwootMetadata = metadata;
        if (!this.pendingOutgoingCalls.has(session.id)) {
          this.pendingOutgoingCalls.set(session.id, request);
        }

        if (this.pendingOutgoingCallRequest === request) {
          this.pendingOutgoingCallRequest = null;
        }
      } catch (error) {
        if (this.pendingOutgoingCallRequest === request) {
          this.pendingOutgoingCallRequest = null;
        }

        reject(error);
        this.notifyError(error.message || 'Falha ao iniciar chamada SIP');
      }
    });
  }

  attachPendingOutgoingRequest(session) {
    const pendingRequest =
      this.pendingOutgoingCalls.get(session.id) ||
      this.pendingOutgoingCallRequest;

    if (!pendingRequest) {
      return null;
    }

    this.pendingOutgoingCalls.set(session.id, pendingRequest);

    if (this.pendingOutgoingCallRequest === pendingRequest) {
      this.pendingOutgoingCallRequest = null;
    }

    return pendingRequest;
  }

  resolvePendingOutgoingCall(sessionId, payload) {
    const pendingRequest = this.pendingOutgoingCalls.get(sessionId);
    if (!pendingRequest) return;

    pendingRequest.resolve?.(payload);
    this.pendingOutgoingCalls.delete(sessionId);
  }

  rejectPendingOutgoingCall(sessionId, error) {
    const pendingRequest = this.pendingOutgoingCalls.get(sessionId);
    if (!pendingRequest) return;

    pendingRequest.reject?.(
      error instanceof Error
        ? error
        : new Error(error || 'Failed to start SIP call')
    );
    this.pendingOutgoingCalls.delete(sessionId);
  }

  // eslint-disable-next-line class-methods-use-this
  mapFailureCauseToCallStatus(cause) {
    const normalizedCause = cause?.toString().toLowerCase() || '';

    if (
      normalizedCause.includes('unavailable') ||
      normalizedCause.includes('rejected') ||
      normalizedCause.includes('cancel') ||
      normalizedCause.includes('no answer')
    ) {
      return 'no-answer';
    }

    return 'failed';
  }

  // eslint-disable-next-line class-methods-use-this
  currentTimestamp() {
    return Math.floor(Date.now() / 1000);
  }

  // eslint-disable-next-line class-methods-use-this
  normalizeCallDirection(direction) {
    return direction === 'incoming' ? 'inbound' : 'outbound';
  }

  /**
   * Retorna estado atual do worker
   */
  getState() {
    return this.state;
  }

  getStatusSnapshot() {
    return {
      state: this.state,
      extension: this.extension,
      inCall: this.currentCall !== null,
      registered: this.ua && this.ua.isRegistered(),
    };
  }

  getCurrentCallSnapshot() {
    if (!this.currentCall) {
      return null;
    }

    const remoteIdentity = this.currentCall.remote_identity?.uri?.user;
    const callerName =
      this.currentCall.remote_identity?.display_name || remoteIdentity;

    return {
      callSid: this.currentCall.id,
      conversationId: this.currentCall.conversation_id,
      inboxId: this.inboxId,
      callDirection: this.normalizeCallDirection(this.currentCall.direction),
      callerId:
        this.currentCall.direction === 'incoming' ? remoteIdentity : null,
      callerName: this.currentCall.direction === 'incoming' ? callerName : null,
      calledNumber:
        this.currentCall.direction === 'outgoing' ? remoteIdentity : null,
      status: this.state,
      isActive: ['calling', 'in_call'].includes(this.state),
      isMuted: this.isMicrophoneMuted(this.currentCall.id),
    };
  }

  /**
   * Verifica se worker está conectado
   */
  isConnected() {
    return this.ua && this.ua.isRegistered();
  }

  /**
   * Verifica se está em chamada
   */
  isInCall() {
    return this.currentCall !== null;
  }
}

export default SipAgentWorker;
